import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PortfolioModal from './components/PortfolioModal';
import { portfolioItems, PortfolioItem } from '@/mocks/portfolio';

type Category = 'all' | 'black-gray' | 'minimal' | 'color' | 'lettering' | 'geometric';

const categories: { key: Category; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'black-gray', label: 'Black & Gray' },
  { key: 'minimal', label: 'Minimal' },
  { key: 'color', label: 'Color' },
  { key: 'lettering', label: 'Lettering' },
  { key: 'geometric', label: 'Geometric' },
];

export default function PortfolioPage() {
  const [active, setActive] = useState<Category>('all');
  const [selected, setSelected] = useState<PortfolioItem | null>(null);
  const [headerVisible, setHeaderVisible] = useState(false);
  const [itemsVisible, setItemsVisible] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setHeaderVisible(true), 100);
    const t2 = setTimeout(() => setItemsVisible(true), 400);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  const filtered = active === 'all' ? portfolioItems : portfolioItems.filter((i) => i.category === active);

  const handleCategoryChange = (key: Category) => {
    setItemsVisible(false);
    setActive(key);
    setTimeout(() => setItemsVisible(true), 200);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/2e5014b68e0d37740717714a756ca072.jpg"
          alt="Portfolio Hero"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/88 via-black/65 to-black/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent" />

        <div className="absolute inset-0 flex flex-col justify-center px-8 md:px-16 lg:px-24">
          <div
            className="transition-all duration-700 ease-out"
            style={{ opacity: headerVisible ? 1 : 0, transform: headerVisible ? 'translateY(0)' : 'translateY(20px)' }}
          >
            <p
              className="text-xs tracking-[0.35em] uppercase text-white/50 mb-4"
              style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}
            >
              Gallery
            </p>
          </div>
          <div
            className="transition-all duration-900 ease-out"
            style={{
              opacity: headerVisible ? 1 : 0,
              transform: headerVisible ? 'translateY(0)' : 'translateY(40px)',
              transitionDelay: '150ms',
              transitionDuration: '900ms',
            }}
          >
            <h1
              className="text-5xl md:text-6xl lg:text-7xl text-white mb-5"
              style={{ fontFamily: "'Cinzel', serif", fontWeight: 300, letterSpacing: '0.12em' }}
            >
              Portfolio
            </h1>
          </div>
          <div
            className="transition-all duration-700 ease-out"
            style={{
              opacity: headerVisible ? 1 : 0,
              transform: headerVisible ? 'translateY(0)' : 'translateY(20px)',
              transitionDelay: '300ms',
            }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-px bg-white/40" />
              <span className="text-white/40 text-[10px] tracking-[0.3em] uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Seonyul Line Studio
              </span>
            </div>
            <p
              className="text-sm md:text-base text-white/60 max-w-md font-light"
              style={{ fontFamily: "'Nanum Myeongjo', serif", letterSpacing: '0.1em', lineHeight: '1.9' }}
            >
              각 작품은 고객과의 깊은 대화에서 시작됩니다.<br />
              개인의 이야기를 피부 위에 새기는 예술.
            </p>
          </div>
        </div>
      </section>

      {/* Filter */}
      <div className="sticky top-16 md:top-20 z-40 bg-white/90 backdrop-blur-xl border-b border-[#E5E5E5]">
        <div className="max-w-[1440px] mx-auto px-8 md:px-16 py-5 flex gap-8 md:gap-12 overflow-x-auto">
          {categories.map((c) => (
            <button
              key={c.key}
              onClick={() => handleCategoryChange(c.key)}
              className={`text-[11px] tracking-[0.2em] uppercase whitespace-nowrap pb-1 transition-all duration-300 cursor-pointer ${
                active === c.key
                  ? 'text-[#222222] font-semibold border-b-2 border-[#222222]'
                  : 'text-[#999] font-normal hover:text-[#222222]'
              }`}
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* Masonry Grid */}
      <section className="py-12 px-8 md:px-16 max-w-[1440px] mx-auto">
        <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
          {filtered.map((item, idx) => (
            <div
              key={item.id}
              className="break-inside-avoid group relative overflow-hidden cursor-pointer"
              onClick={() => setSelected(item)}
              style={{
                opacity: itemsVisible ? 1 : 0,
                transform: itemsVisible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.97)',
                transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
                transitionDelay: `${(idx % 8) * 60}ms`,
              }}
            >
              <img
                src={item.imageUrl}
                alt={item.style}
                className="w-full object-cover object-top block transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/70 transition-all duration-400 flex flex-col items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                <p className="text-white text-sm font-semibold tracking-widest uppercase">{item.artist}</p>
                <p className="text-white/70 text-xs tracking-wider">{item.bodyPart}</p>
                <span className="mt-3 px-4 py-1.5 border border-white text-white text-xs tracking-widest uppercase hover:bg-white hover:text-[#1A1A1A] transition-all duration-300">
                  View Details
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {selected && <PortfolioModal item={selected} onClose={() => setSelected(null)} />}
      <Footer />
    </div>
  );
}
